import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CreditModeProvider, useCreditMode } from './CreditModeContext';
import { monthOptions } from './aiUsageShared';
import { GrantCreditsModal, IncreaseTotalModal, ChangeCreditsModal } from './CreditModals';
import DailyUsageChart from './DailyUsageChart';
import DonutChart from './DonutChart';
import { mapBalance, mapDailyUsage, fillDailyGaps } from './creditFieldMap';
import {
  getSchoolBalance,
  getGroupBalance,
  getDailyUsage,
} from '../../services/api/eiCreditsService';
import { currentPeriod, monthLabel, formatPercent } from '../../utils/creditFormat';
import {
  sa,
  Card,
  DataTable,
  ProgressBar,
  PillButton,
  SelectPill,
  CreditCostToggle,
  SectionHeaderRow,
  SectionTitle,
  SectionStates,
} from '../superAdminUi';

// AI Usage & Credits tab for the account views (Figma "School / Group AI Usage"). Adapts to
// `scope`: a school shows a per-user table; a group adds a per-school credit breakdown and the
// School / Threads / Messages columns. Credit vs Cost display is shared through
// CreditModeContext; currency is handled solely by the formatters — no symbol is hard-coded.

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
`;

// --- Summary tiles ---------------------------------------------------------
const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Tile = styled.div`
  background: ${(p) => p.$bg};
  border-radius: ${sa.radius.md};
  padding: 18px 24px;
`;
const TileLabel = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 16px;  
  color: ${sa.color.black};
  margin: 0;
`;
const TileValue = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.extrabold};
  font-size: 24px;
  line-height: 1.1;
  color: ${(p) => p.$color};
  margin: 8px 0 0;
`;

function SummaryTile({ label, value, bg, color }) {
  return (
    <Tile $bg={bg}>
      <TileLabel>{label}</TileLabel>
      <TileValue $color={color}>{value}</TileValue>
    </Tile>
  );
}
SummaryTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  bg: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

// --- Credit-usage summary bar ----------------------------------------------
const Between = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Bold = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.text};
`;
const Muted = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.regular};
  font-size: ${(p) => (p.$small ? '12px' : '13px')};
  color: ${sa.color.textGray};
`;

// --- Per-school breakdown (group scope) ------------------------------------
const SchoolRow = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid ${sa.color.stroke};

  &:last-of-type {
    border-bottom: none;
  }
`;
const SchoolActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
`;

// --- Today AI Usage --------------------------------------------------------
const DonutHolder = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0 26px;
`;
const MiniCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const MiniLabel = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.regular};
  font-size: 12px;
  color: ${sa.color.textGray};
  margin: 0;
`;
const MiniValue = styled.p`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 18px;
  color: ${sa.color.text};
  margin: 6px 0 0;
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};
  padding: 4px 6px;
`;
const StepButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  color: ${sa.color.text};
  line-height: 0;

  &:disabled {
    color: ${sa.color.textGray};
    opacity: 0.4;
    cursor: default;
  }
  svg {
    font-size: 18px;
  }
`;
const StepDate = styled.span`
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 13px;
  color: ${sa.color.text};
  min-width: 108px;
  text-align: center;
`;

const asyncRow = (asyncPot) =>
  asyncPot ? { ...asyncPot, name: 'System / async tasks', isAsync: true } : null;

// Local, London-time "today" as YYYY-MM-DD so the donut opens on the current day when it falls
// within the loaded period (the daily series is gap-filled for the whole month).
const londonToday = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const longDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

const renewalLabel = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    day: '2-digit',
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

function AiUsageInner({ scope, schoolId, groupId }) {
  const history = useHistory();
  const { format, mode, setMode } = useCreditMode();
  const isGroup = scope === 'group';
  const entityId = isGroup ? groupId : schoolId;

  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState([]);
  const [dayIndex, setDayIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { type, user?, schoolId?, name? }

  const months = useMemo(
    () => monthOptions().map((p) => ({ value: p, label: monthLabel(p) })),
    []
  );

  const load = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      setError('No account selected.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [balanceRaw, dailyRaw] = await Promise.all([
        isGroup ? getGroupBalance(entityId, period) : getSchoolBalance(entityId, period),
        getDailyUsage({ scope, id: entityId, months: [period] }),
      ]);
      setData(mapBalance(balanceRaw));
      setDaily(fillDailyGaps(mapDailyUsage(dailyRaw), period));
    } catch (err) {
      setError(err?.message || 'Could not load AI usage for this period.');
      setData(null);
      setDaily([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, isGroup, scope, period]);

  useEffect(() => {
    load();
  }, [load]);

  // Open the donut on today when it sits in the loaded month, otherwise on the latest day.
  useEffect(() => {
    if (!daily.length) {
      setDayIndex(0);
      return;
    }
    const today = londonToday();
    const idx = daily.findIndex((d) => d.date === today);
    setDayIndex(idx >= 0 ? idx : daily.length - 1);
  }, [daily]);

  const openAnalytics = (user) => {
    const forScope = user.schoolId ? 'school' : scope;
    const forId = user.schoolId || entityId;
    history.push(
      `/ai-usage-analytics?userId=${encodeURIComponent(user.userId)}&scope=${forScope}&id=${encodeURIComponent(forId)}`
    );
  };

  // Refresh the balances in the background but leave the modal mounted so it can show its own
  // success confirmation; the modal's Done/close is what dismisses it.
  const onModalSuccess = () => {
    load();
  };

  const users = data?.users || [];
  const schools = data?.schools || [];
  const asyncTasks = asyncRow(data?.asyncPot);
  const isEmpty = !data || (data.total === 0 && users.length === 0);

  // The per-day fair share of the monthly allowance drives the Today donut + the daily chart's
  // 0–100% axis. Derived from the loaded balance — there is no separate daily-limit endpoint.
  const dailyLimit = daily.length ? Math.round((data?.total || 0) / daily.length) : 0;
  const selectedDay = daily[dayIndex] || null;
  const todayUsed = selectedDay?.credits || 0;
  const todayRemaining = Math.max(0, dailyLimit - todayUsed);
  const todayPct = dailyLimit > 0 ? (todayUsed / dailyLimit) * 100 : 0;

  const userColumns = useMemo(() => {
    const cols = [
      { key: 'name', label: 'User Name', width: '220px', render: (r) => <Bold>{r.name || '—'}</Bold> },
      {
        key: 'usage',
        label: 'Credit Usage',
        width: '140px',
        render: (r) => (r.isAsync ? '—' : formatPercent(r.usagePct)),
      },
    ];
    if (isGroup) {
      cols.push(
        { key: 'school', label: 'School Name', width: '180px', render: (r) => (r.isAsync ? '—' : r.schoolName || '—') },
        { key: 'threads', label: 'No of Threads', width: '130px', render: (r) => (r.isAsync ? '—' : r.threads) },
        { key: 'messages', label: 'No of Messages', width: '140px', render: (r) => (r.isAsync ? '—' : r.messages) }
      );
    }
    cols.push({
      key: 'action',
      label: 'Action',
      align: 'right',
      render: (r) =>
        r.isAsync ? (
          '—'
        ) : (
          <SchoolActions>
            <PillButton
              type='button'
              onClick={() => setModal({ type: 'change', user: r, schoolId: r.schoolId || schoolId })}
            >
              Change Credits
            </PillButton>
            <PillButton type='button' $variant='outline' onClick={() => openAnalytics(r)}>
              Detail
            </PillButton>
          </SchoolActions>
        ),
    });
    return cols;
    // openAnalytics/setModal are stable enough for this presentational table.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, schoolId]);

  // School scope appends the pooled async row to the users table; group scope shows it in the
  // per-school breakdown instead.
  const userRows = !isGroup && asyncTasks ? [...users, asyncTasks] : users;

  return (
    <Card>
      <Stack data-testid='ai-usage-section'>
        <SectionHeaderRow title='AI Usage & Credits' size='md'>
          <CreditCostToggle value={mode} onChange={setMode} />
          {isGroup && (
            <PillButton type='button' $variant='purple' onClick={() => setModal({ type: 'increase-group' })}>
              Increase Group Total
            </PillButton>
          )}
        </SectionHeaderRow>

        <SectionStates loading={loading} error={error} isEmpty={isEmpty} onRetry={load} />

        {!loading && !error && !isEmpty && data && (
          <>
            <Tiles>
              <SummaryTile
                label='Current Balance'
                value={format(data.remaining)}
                bg='#DBF5FF'
                color={sa.color.blue}
              />
              <SummaryTile
                label='Total Allowance'
                value={format(data.total)}
                bg='#fbe3e8'
                color={sa.color.pink}
              />
              <SummaryTile
                label='Total Usage'
                value={formatPercent(data.usagePct)}
                bg='#dcefef'
                color={sa.color.teal}
              />
            </Tiles>

            <Card $pad='20px 24px'>
              <Between>
                <Bold>Credit Usage</Bold>
                <Muted>{formatPercent(data.usagePct)} used this month</Muted>
              </Between>
              <div style={{ margin: '12px 0' }}>
                <ProgressBar pct={data.usagePct} showLabel={false} />
              </div>
              <Bold>
                {format(data.used, { compact: false })}/{format(data.total, { compact: false })} monthly
                credits used
              </Bold>
              {data.renewalDate && (
                <div style={{ marginTop: 4 }}>
                  <Muted $small>Monthly credits renew on {renewalLabel(data.renewalDate)}</Muted>
                </div>
              )}

              {isGroup && schools.length > 0 && (
                <div style={{ marginTop: 20, borderTop: `1px solid ${sa.color.stroke}`, paddingTop: 16 }}>
                  {schools.map((s) => (
                    <SchoolRow key={s.schoolId}>
                      <Between>
                        <Bold>{s.name}</Bold>
                        <Muted>{formatPercent(s.usagePct)} used this month</Muted>
                      </Between>
                      <div className="mt-10">
                        <ProgressBar pct={s.usagePct} showLabel={false} />
                      </div>
                      <SchoolActions>
                        <PillButton
                          type='button'
                          $variant='danger'
                          onClick={() =>
                            setModal({ type: 'grant', schoolId: s.schoolId, name: s.name, user: null })
                          }
                        >
                          Grant Credits
                        </PillButton>
                        <PillButton
                          type='button'
                          $variant='purple'
                          onClick={() =>
                            setModal({ type: 'increase-school', schoolId: s.schoolId, name: s.name })
                          }
                        >
                          Increase School Total
                        </PillButton>
                      </SchoolActions>
                    </SchoolRow>
                  ))}
                  {asyncTasks && (
                    <div className="mt-12">
                      <Muted $small>
                        System / async tasks: {format(asyncTasks.used)} credits
                      </Muted>
                    </div>
                  )}
                </div>
              )}
            </Card>

            <div>
              <SectionHeaderRow title='Users AI Usage' align='end'>
                <SelectPill
                  value={period}
                  onChange={setPeriod}
                  options={months}
                  ariaLabel='Month'
                  testId='month-dropdown'
                />
              </SectionHeaderRow>
              <div style={{ marginTop: sa.space.section }}>
                <DataTable
                  columns={userColumns}
                  rows={userRows}
                  getRowKey={(r) => (r.isAsync ? 'async' : r.userId)}
                  empty='No users have recorded usage this period.'
                />
              </div>
            </div>

            <Card>
              <Between>
                <SectionTitle>Today AI Usage</SectionTitle>
                <Stepper>
                  <StepButton
                    type='button'
                    aria-label='Previous day'
                    disabled={dayIndex <= 0}
                    onClick={() => setDayIndex((i) => Math.max(0, i - 1))}
                  >
                    <ChevronLeftIcon />
                  </StepButton>
                  <StepDate>{longDate(selectedDay?.date)}</StepDate>
                  <StepButton
                    type='button'
                    aria-label='Next day'
                    disabled={dayIndex >= daily.length - 1}
                    onClick={() => setDayIndex((i) => Math.min(daily.length - 1, i + 1))}
                  >
                    <ChevronRightIcon />
                  </StepButton>
                </Stepper>
              </Between>
              <DonutHolder>
                <DonutChart pct={todayPct} />
              </DonutHolder>
              <MiniCards>
                <Card $pad='14px 16px'>
                  <MiniLabel>Credits Used</MiniLabel>
                  <MiniValue>{format(todayUsed)}</MiniValue>
                </Card>
                <Card $pad='14px 16px'>
                  <MiniLabel>Credits Remaining</MiniLabel>
                  <MiniValue>{format(todayRemaining)}</MiniValue>
                </Card>
                <Card $pad='14px 16px'>
                  <MiniLabel>Total Daily Limit</MiniLabel>
                  <MiniValue>{format(dailyLimit)}</MiniValue>
                </Card>
              </MiniCards>
            </Card>

            <Card>
              <SectionHeaderRow title='Daily AI Usage' align='end'>
                <SelectPill
                  value={period}
                  onChange={setPeriod}
                  options={months}
                  ariaLabel='Month'
                  testId='daily-month-dropdown'
                />
              </SectionHeaderRow>
              <div style={{ marginTop: sa.space.section }}>
                <DailyUsageChart days={daily} mode={mode} limit={dailyLimit} />
              </div>
            </Card>
          </>
        )}

        <GrantCreditsModal
          open={modal?.type === 'grant'}
          onClose={() => setModal(null)}
          onSuccess={onModalSuccess}
          period={period}
          schoolId={modal?.schoolId || schoolId}
          user={modal?.user}
        />
        <ChangeCreditsModal
          open={modal?.type === 'change'}
          onClose={() => setModal(null)}
          onSuccess={onModalSuccess}
          period={period}
          schoolId={modal?.schoolId || schoolId}
          user={modal?.user}
        />
        <IncreaseTotalModal
          open={modal?.type === 'increase-school'}
          onClose={() => setModal(null)}
          onSuccess={onModalSuccess}
          scope='school'
          period={period}
          schoolId={modal?.schoolId || schoolId}
          entityName={modal?.name}
        />
        <IncreaseTotalModal
          open={modal?.type === 'increase-group'}
          onClose={() => setModal(null)}
          onSuccess={onModalSuccess}
          scope='group'
          period={period}
          groupId={groupId}
          entityName={null}
        />
      </Stack>
    </Card>
  );
}

AiUsageInner.propTypes = {
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function AiUsageSection({ scope, schoolId, groupId }) {
  return (
    <CreditModeProvider>
      <AiUsageInner scope={scope} schoolId={schoolId} groupId={groupId} />
    </CreditModeProvider>
  );
}

AiUsageSection.propTypes = {
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AiUsageSection;
